import React, { useEffect, useReducer, useRef, useState } from 'react';
import PreDeployConfirmModal from '../components/PreDeployConfirmModal';
import { RunAssistance } from '@back4app2/sdk';
import { Link, Outlet, useLocation, useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { App, AppStatus, AppStatusError, AuthenticationError, AuthorizationError, BlockedResourceError, CustomDomainStatus, DisposableEmailError, NetworkError, NotFoundError, PaymentPendingError, ServiceEnvironment, User, UserVerificationPendingError, DeploymentStatusError } from '@back4app2/sdk';
import { BACK4APP_DOT_COM_SITE_URL, BACK4APP_DOT_COM_DASHBOARD_URL } from '../settings';
import back4app2 from '../back4app2';
import LoadingSpinner from '../components/LoadingSpinner';
import MyAppStatus from '../components/MyAppStatus';
import { ReactComponent as LinkIconSVG } from '../assets/images/link-icon.svg';
import { ReactComponent as StatusErrorSVG } from '../assets/images/status-error.svg';
import { ReactComponent as StatusSpinnerSVG } from '../assets/images/status-spinner.svg';
import { ReactComponent as StatusSuccessSVG } from '../assets/images/status-success.svg';
import ErrorMessage from '../components/ErrorMessage';
import MyAppActions from '../components/MyAppActions';
import { toast } from 'react-hot-toast';
import { DsButton, Eyebrow, StatusDot, StatusPill, SurfaceCard } from '../components/ds';
import {
  ArrowLeftIcon, BranchIcon, DeploymentsIcon, LogsIcon, MetricsIcon, OverviewIcon, SettingsIcon,
} from '../components/ds/icons';
import { TOAST_SURFACE } from '../components/ds/toast';
import { AmplitudeEvent, trackEvent } from '../utils/amplitude';
import { hasWritePermission } from '../utils/permissions';

interface MyAppState {
  isLoading: boolean;
  loadingErrorMessage?: string;
  app?: App;
  appLoadedAt?: Date;
  isDeletingApp: boolean;
}

/**
 * Runs the analysis, then decides what the Deploy button does.
 *
 * Wired only into the explicit Deploy actions, not into "save settings and
 * redeploy" or "domain changed, redeploy": someone who has just configured the
 * app does not need to be asked about its configuration, and a screen that
 * appears on every path is one nobody reads.
 */
const runPreDeploy = async (serviceEnvironmentId: string) => {
  try {
    return await back4app2.prepareDeployment(serviceEnvironmentId);
  } catch (e) {
    // Never block the deploy on the pre-check: it decides what to SHOW, and
    // the worker gate is what actually enforces. Failing here would turn an
    // analyzer problem into an inability to deploy.
    return { blocked: false, needsConfirmation: false };
  }
};

const INITIAL_STATE: MyAppState = {
  isLoading: true,
  isDeletingApp: false
};

enum MyAppActionType {
  RESET,
  FINISH_LOADING,
  SET_IS_DELETING_APP
}

const reset = () => ({
  type: MyAppActionType.RESET
} as const);

const finishLoading = (errorMessage?: string, app?: App) => ({
  type: MyAppActionType.FINISH_LOADING,
  payload: {
    errorMessage,
    app
  }
} as const);

const setIsDeletingApp = (isDeleting: boolean) => ({
  type: MyAppActionType.SET_IS_DELETING_APP,
  payload: isDeleting
} as const);

type MyAppAction = ReturnType<typeof reset> | ReturnType<typeof finishLoading> | ReturnType<typeof setIsDeletingApp>;

const reducer = (state: MyAppState = INITIAL_STATE, action: MyAppAction): MyAppState => {
  switch (action.type) {
    case MyAppActionType.RESET:
      return {
        ...INITIAL_STATE,
      };
    
    case MyAppActionType.FINISH_LOADING: {
      if (action.payload.errorMessage === 'App not found.' && state.isDeletingApp) {
        return {
          ...state,
          isLoading: false,
          appLoadedAt: new Date()
        }
      }
      return {
        ...state,
        isLoading: false,
        loadingErrorMessage: action.payload.errorMessage,
        app: action.payload.app,
        appLoadedAt: new Date()
      }
    }
    
    case MyAppActionType.SET_IS_DELETING_APP:
      return {
        ...state,
        isDeletingApp: action.payload
      }
  }
};

const MyApp = () => {
  const { appId } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const [pendingDeploy, setPendingDeploy] = useState<{
    serviceEnvironmentId: string;
    runAssistance: RunAssistance;
  } | null>(null);
  const { isLoading, loadingErrorMessage, app, appLoadedAt } = state;
  const mainRef = useRef<HTMLDivElement>(null);
  const { user, freePlanUsageHours } = useOutletContext<{ user: User, freePlanUsageHours: number }>();

  useEffect(
    () => {
      const subscription = back4app2.subscribeToApp(appId as string, (error, snapshot) => {
        if (error) {
          if (error instanceof NetworkError) {
            console.error('network error', error);
            dispatch(finishLoading('Network error when loading app. Check your internet connection and try again.'));
          } else if (error instanceof AuthenticationError || error instanceof AuthorizationError) {
            window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
          } else if (error instanceof NotFoundError) {
            dispatch(finishLoading('App not found.'));
          } else {
            console.error('unexpected error loading app', error);
            dispatch(finishLoading('Unexpected error when loading app. Please try again.'));
          }
        } else {
          dispatch(finishLoading(undefined, snapshot));
        }        
      });

      return () => {
        subscription.unsubscribe();
        dispatch(reset());
      }
    },
    [appId]
  );

  const updateIsDeletingApp = (isDeleting: boolean) => dispatch(setIsDeletingApp(isDeleting));

  const redeployApp = async (mainServiceEnvironment: ServiceEnvironment | undefined, appId: string,) => {
    if (!app) {
      return;
    }

    if (!app.isOwner) {
      toast.error('You don\'t have permission to trigger deployments.', {
        className: TOAST_SURFACE,
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 4000
      });
      return;
    }

    if (!hasWritePermission(app, 'Deploy')) {
      toast.error('You don\'t have permission to trigger deployments.', {
        className: TOAST_SURFACE,
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 4000
      });
      return;
    }

    trackEvent(AmplitudeEvent.DEPLOY_LATEST_COMMIT);
    const toastId = toast.loading('Deploying latest commit...', {
      className: TOAST_SURFACE,
      icon: <StatusSpinnerSVG width="24px" height="24px" color="text-old-blue" className="animate-spin" />
    });
    try {
      const prepared = await runPreDeploy(
        mainServiceEnvironment?.id as string
      );

      if (prepared.blocked) {
        // The block card on the overview carries the explanation and the
        // appeal; a toast would only repeat it worse.
        toast.dismiss(toastId);
        navigate(`/apps/${appId}`);
        return;
      }

      if (prepared.needsConfirmation && prepared.runAssistance) {
        toast.dismiss(toastId);
        setPendingDeploy({
          serviceEnvironmentId: mainServiceEnvironment?.id as string,
          runAssistance: prepared.runAssistance,
        });
        return;
      }

      await back4app2.triggerManualDeployment(mainServiceEnvironment?.id as string);
      toast.success('New deployment created!', {
        className: TOAST_SURFACE,
        id: toastId,
        icon: <StatusSuccessSVG width="24px" height="24px" className="animate-bounce-in top-" />
      });
      navigate(`/apps/${appId}`);
    } catch (err: any) {
      let errMsg = 'Something went wrong!';
      if (err instanceof AppStatusError || err instanceof UserVerificationPendingError || err instanceof BlockedResourceError || err instanceof PaymentPendingError || err instanceof NotFoundError || err instanceof DeploymentStatusError || err instanceof AuthorizationError) {
        errMsg = err.message;
      }
      toast.error(errMsg, {
        className: TOAST_SURFACE.replace('max-w-xs', 'min-w-xs'),
        id: toastId,
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 6000
      });
    }
  }
  

  useEffect(() => {
    if (app?.status === AppStatus.PENDING_VERIFICATION && app?.error && app.error.code === DisposableEmailError.CODE) {
      toast.error(app.error.message, {
        className:`bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-3xl text-sm`,
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in flex-none" />,
        duration: 5000
      });
    }
  }, [app])

  if (isLoading) {
    return (<>
      <div className="grow flex flex-col justify-center items-center">
        <LoadingSpinner />
        <div className="mt-4 text-ds-135 text-navy-200">
          Loading app
        </div>
      </div>
    </>);
  }

  if (loadingErrorMessage) {
    return (<>
      <ErrorMessage message={loadingErrorMessage} />
    </>);
  }

  const env = app!.mainService && app!.mainService.mainServiceEnvironment;
  const customDomain = env && env.mainCustomDomain;
  const plan = env && env.plan;
  const planRAM = plan?.maxRAM
    ? (plan.maxRAM >= 1024 ? `${plan.maxRAM / 1024}GB` : `${plan.maxRAM}MB`)
    : undefined;

  const sections = [
    { label: 'Overview', short: 'Overview', Icon: OverviewIcon, url: `/apps/${appId}` },
    { label: 'Deployments', short: 'Deploys', Icon: DeploymentsIcon, url: `/apps/${appId}/deployments` },
    { label: 'Logs', short: 'Logs', Icon: LogsIcon, url: `/apps/${appId}/logs` },
    { label: 'Metrics', short: 'Metrics', Icon: MetricsIcon, url: `/apps/${appId}/metrics` },
    { label: 'Settings', short: 'Settings', Icon: SettingsIcon, url: `/apps/${appId}/settings` },
  ];

  const goBackToApps = () => { window.location.href = `${BACK4APP_DOT_COM_DASHBOARD_URL}/apps`; };

  /** The repository / image the app runs, and the URL it is served on. */
  const sourceLines = env ? (
    <div className="flex flex-col gap-2 min-w-0">
      {app!.mainService!.repository ? (<>
        <a
          target="_blank"
          rel="noreferrer"
          href={`https://www.github.com/${app!.mainService!.repository!.fullName}`}
          className="b4-focus font-courrier text-ds-115 text-navy-300 hover:text-action-soft truncate"
        >
          {app!.mainService!.repository!.fullName}
        </a>
        <div className="flex items-center gap-1.5 min-w-0 font-courrier text-ds-115 text-navy-300">
          <BranchIcon size={12} />
          <span className="truncate">{env.branchName}</span>
        </div>
      </>) : (<>
        <span className="font-courrier text-ds-115 text-navy-300">Docker Hub</span>
        <span className="font-courrier text-ds-115 text-navy-100 truncate">
          {env.imageRepository}:{env.imageTag}
        </span>
      </>)}
      {/* A blocked app must not advertise a live address. The URL is hidden
          wherever it appears while the security review is open — here, in the
          app list, and in the deploy summary on the overview. */}
      {customDomain && customDomain.status !== CustomDomainStatus.EXPIRED && !app!.block && (
        <a
          target="_blank"
          rel="noreferrer"
          href={`${window.location.protocol}//${customDomain.name}`}
          className="b4-focus inline-flex items-center gap-1.5 min-w-0 font-courrier text-ds-115 text-state-ok-soft hover:text-white"
        >
          <span className="truncate">{customDomain.name}</span>
          <LinkIconSVG className="flex-none" />
        </a>
      )}
    </div>
  ) : null;

  /**
   * The temporary-URL notice.
   *
   * Overview only, and nowhere else: it is an advert for a plan, and repeating
   * it above Deployments, Logs, Metrics and Settings put it on top of every
   * screen in the app. The Overview is where "is it up, and for how long?" is
   * the question being asked.
   */
  const domainStatus = customDomain?.status;
  const isFreePlan = !!plan?.name.includes('Free');
  const isOverview = pathname === `/apps/${appId}` || pathname === `/apps/${appId}/`;

  // 44px targets on a phone, the 38px control height from 1024 up.
  const CTA_SIZE = 'lg:!h-[38px]';

  const renderStatusNotice = () => {
    // "Temporary URL active" over a Blocked pill is two answers to the same
    // question. While the review is open the URL story is not the story.
    if (app!.block) {
      return null;
    }

    if (domainStatus === CustomDomainStatus.EXPIRED) {
      return (
        <SurfaceCard elevated className="p-4 lg:px-[18px] flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-6">
          <div className="flex-1 min-w-0 flex flex-col gap-1.5">
            <div className="flex items-center gap-2.5">
              <StatusDot tone="warn" halo />
              <span className="font-sora text-ds-15 font-semibold tracking-[-0.01em] text-white">
                URL expired
              </span>
              <span className="font-courrier text-ds-115 text-navy-300 truncate">
                temporary URL on the free plan
              </span>
            </div>
            <span className="text-ds-135 leading-[1.5] text-navy-100">
              Redeploy to keep experimenting, or upgrade for a URL that stays.
            </span>
          </div>
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:flex-none">
            <DsButton
              variant="primary"
              size="touch"
              className={CTA_SIZE}
              isDisabled={!app?.isOwner}
              onClick={() => env && redeployApp(env, appId as string)}
            >
              Redeploy app
            </DsButton>
            <DsButton
              variant="action"
              size="touch"
              className={CTA_SIZE}
              onClick={() => navigate(`/apps/${appId}/settings#general-settings`)}
            >
              Upgrade for a permanent URL
            </DsButton>
          </div>
        </SurfaceCard>
      );
    }

    if (domainStatus === CustomDomainStatus.READY && isFreePlan) {
      return (
        <SurfaceCard elevated className="p-4 lg:px-[18px] flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-6">
          <div className="flex-1 min-w-0 flex flex-col gap-1.5">
            <div className="flex items-center gap-2.5">
              <StatusDot tone="ok" halo />
              <span className="font-sora text-ds-15 font-semibold tracking-[-0.01em] text-white">
                Temporary URL active
              </span>
              <span className="font-courrier text-ds-115 text-navy-300 truncate">
                live for 60 minutes
              </span>
            </div>
            <span className="text-ds-135 leading-[1.5] text-navy-100">
              This URL is temporary. Upgrade for one that stays.
            </span>
          </div>
          <DsButton
            variant="action"
            size="touch"
            className={`${CTA_SIZE} lg:flex-none`}
            onClick={() => navigate(`/apps/${appId}/settings#general-settings`)}
          >
            Upgrade for a permanent URL
          </DsButton>
        </SurfaceCard>
      );
    }

    return null;
  };

  return (<>
    <div className="flex-1 min-h-0 flex bg-navy-900">

      {/* ── Sidebar: desktop only. On mobile it becomes the context header
             plus the bottom tab bar below. ─────────────────────────────── */}
      <aside className="hidden lg:flex flex-none w-[232px] bg-navy-800 border-r border-navy-700 flex-col overflow-y-auto b4-thin">
        <div className="px-3 py-5 border-b border-navy-700 flex flex-col gap-4">
          <div className="flex items-center justify-between px-1">
            <button
              type="button"
              onClick={goBackToApps}
              className="b4-focus inline-flex items-center gap-1.5 rounded-control px-1 py-0.5 -ml-1 text-[13px] font-medium text-navy-200 hover:text-white transition-colors duration-[120ms]"
            >
              <ArrowLeftIcon size={14} />
              All apps
            </button>
            <StatusPill tone="info">Beta</StatusPill>
          </div>

          <div className="px-1 flex flex-col gap-2">
            <Eyebrow>container</Eyebrow>
            <div className="font-sora text-ds-135 font-semibold tracking-[-0.01em] text-white truncate" title={app!.name}>
              {app!.name}
            </div>
          </div>
          <div className="px-1">{sourceLines}</div>
          <MyAppActions app={app!} />
          <div className="px-1"><MyAppStatus app={app!} variant="pill" /></div>
        </div>

        <div className="px-2 py-4 flex flex-col gap-1">
          {sections.map(section => (
            <MenuItem key={section.url} Icon={section.Icon} title={section.label} url={section.url} />
          ))}
        </div>

        {plan ? (
          <div className="mt-auto px-4 pt-4 pb-5 border-t border-navy-700 flex flex-col gap-2.5">
            <Eyebrow>plan</Eyebrow>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-ds-135 font-semibold text-white">{plan.name}</span>
              <button
                type="button"
                onClick={() => navigate(`/apps/${appId}/settings#general-settings`)}
                className="b4-focus rounded-control text-ds-125 font-semibold text-action-soft hover:text-white"
              >
                Upgrade
              </button>
            </div>
            <span className="font-courrier text-ds-115 leading-[1.5] text-navy-200">
              {[planRAM, plan.maxCPU ? `${plan.maxCPU} CPU` : undefined].filter(Boolean).join(' · ')}
            </span>
          </div>
        ) : null}
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">

        {/* ── Mobile context header ──────────────────────────────────────── */}
        <div className="lg:hidden flex-none bg-navy-800 border-b border-navy-500 px-4 pt-3 pb-4 flex items-center gap-2.5">
          <button
            type="button"
            aria-label="Back to all apps"
            onClick={goBackToApps}
            className="b4-focus w-11 h-11 flex-none -ml-2.5 rounded-control text-navy-200 flex items-center justify-center hover:bg-navy-600"
          >
            <ArrowLeftIcon size={18} />
          </button>
          <div className="flex-1 min-w-0 flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <StatusPill tone="info">Beta</StatusPill>
              <Eyebrow>container</Eyebrow>
            </div>
            <div className="font-sora text-ds-15 font-semibold tracking-[-0.01em] text-white truncate">
              {app!.name}
            </div>
          </div>
          {/* Status over Action on the right, aligned with the two lines on the
              left. The Action dropdown used to be a full-width row of its own
              under all of this, which made a secondary control the widest thing
              on the screen. */}
          <div className="flex-none flex flex-col items-end gap-2">
            <MyAppStatus app={app!} variant="pill" />
            <MyAppActions app={app!} compact />
          </div>
        </div>

        {/* pb-24 keeps the last row clear of the bottom tab bar */}
        <main
          className="flex-1 min-h-0 overflow-y-auto b4-thin px-4 pt-4 pb-24 lg:px-8 lg:pt-7 lg:pb-10 flex flex-col gap-5 lg:gap-6 [&>*]:min-w-0"
          ref={mainRef}
        >
          {isOverview ? renderStatusNotice() : null}
          <Outlet context={{ app, appLoadedAt, mainRef, user, freePlanUsageHours, updateIsDeletingApp }} />
        </main>

        {/* ── Mobile tab bar. Five destinations, same five the sidebar has —
               nothing moves into a "More" sheet. ─────────────────────────── */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-navy-800 border-t border-navy-500 px-2 pt-1.5 pb-4 flex gap-0.5 [padding-bottom:calc(1rem+env(safe-area-inset-bottom))]">
          {sections.map(section => (
            <TabItem key={section.url} Icon={section.Icon} title={section.short} url={section.url} />
          ))}
        </nav>
      </div>
    </div>
    {pendingDeploy && app?.mainService?.mainServiceEnvironment ? (
      <PreDeployConfirmModal
        appId={appId as string}
        serviceEnvironment={app.mainService.mainServiceEnvironment}
        runAssistance={pendingDeploy.runAssistance}
        onCancel={() => setPendingDeploy(null)}
        onProceed={async () => {
          const { serviceEnvironmentId } = pendingDeploy;
          setPendingDeploy(null);
          await back4app2.triggerManualDeployment(serviceEnvironmentId);
          navigate(`/apps/${appId}`);
        }}
      />
    ) : null}
  </>);
};

interface NavItemProps {
  Icon: (props: { size?: number, className?: string }) => JSX.Element;
  title: string;
  url: string;
}

/** Overview is the index route, so it only matches exactly. */
const useIsActive = (url: string) => {
  const { pathname } = useLocation();
  return pathname === url || pathname === `${url}/`;
};

const MenuItem = ({ Icon, title, url }: NavItemProps) => {
  const isActive = useIsActive(url);

  return (
    <Link
      to={url}
      className={[
        'b4-focus flex items-center gap-2.5 h-[38px] px-2.5 rounded-control text-ds-135',
        'transition-colors duration-[120ms]',
        isActive ? 'bg-navy-600 text-white font-semibold' : 'text-navy-200 font-medium hover:text-white hover:bg-navy-600/50',
      ].join(' ')}
    >
      <Icon size={16} />
      {title}
    </Link>
  );
};

const TabItem = ({ Icon, title, url }: NavItemProps) => {
  const isActive = useIsActive(url);

  return (
    <Link
      to={url}
      className={[
        'b4-focus flex-1 min-h-[52px] flex flex-col items-center justify-center gap-1 py-1.5 rounded-surface',
        'uppercase text-ds-105 font-semibold tracking-[0.04em] transition-colors duration-[120ms]',
        isActive ? 'bg-navy-600 text-white' : 'text-navy-300',
      ].join(' ')}
    >
      <Icon size={18} />
      {title}
    </Link>
  );
};

export default MyApp;
